import { useMemo } from "react"
import type { ChatSession } from "@stockline/shared/types/ai/chat-session"
import { CHAT_NOTIFICATION_TYPE } from "@stockline/shared/types/ai/chat-session"
import { useChatSessions } from "./useChatSessions"

export interface BiNotifications {
  /** Every BI brief (`notificationType === "bi"`), newest first. */
  briefs: ChatSession[]
  /** Total UNREAD BI briefs — the steering nav badge count. */
  unreadCount: number
  /**
   * Dashboard ids that have at least one UNREAD brief — the set the tab bar
   * reads to show a per-dashboard unread dot. A brief attributed to several
   * dashboards lights EACH of them. Derived from the same chat-session `unread`
   * flag as the nav badge and the panel dot, so all three stay perfectly
   * consistent (they clear together when a brief is read).
   */
  unreadDashboardIds: Set<string>
  /**
   * Briefs to surface on a given dashboard: those attributed to it (via any of
   * its `sourceDashboardIds`, or the scalar `sourceDashboardId`), PLUS
   * unattributed briefs so nothing is lost (Constitution §2.1.8). A brief that
   * spans several dashboards surfaces on each. Briefs attributed only to OTHER
   * dashboards are excluded — they are reached via their own tab dot / the nav
   * badge.
   */
  forDashboard: (dashboardId: string | null) => ChatSession[]
}

/**
 * The dashboards a brief is attributed to. Prefers the plural
 * `sourceDashboardIds` (a brief may span several dashboards, and it should
 * light each one's dot / show in each scoped panel), falling back to the scalar
 * `sourceDashboardId` for back-compat when the array is null/empty. Returns an
 * empty array for an unattributed brief.
 */
export function dashboardIdsOf(session: ChatSession): string[] {
  if (session.sourceDashboardIds && session.sourceDashboardIds.length > 0) {
    return session.sourceDashboardIds
  }
  return session.sourceDashboardId ? [session.sourceDashboardId] : []
}

/**
 * Pure derivation of the BI surfacing state from the full chat-session pool.
 * Kept separate from the hook so it can be unit-tested without React. Every
 * surfacing level (badge / tab dot / scoped panel) is derived HERE from the one
 * chat-session `unread` flag, so they stay perfectly consistent.
 */
export function deriveBiNotifications(
  sessions: ChatSession[]
): BiNotifications {
  const briefs = sessions.filter(
    (s) => s.notificationType === CHAT_NOTIFICATION_TYPE.BI
  )
  const unread = briefs.filter((s) => s.unread)
  const unreadDashboardIds = new Set<string>()
  for (const s of unread) {
    for (const id of dashboardIdsOf(s)) unreadDashboardIds.add(id)
  }
  const forDashboard = (dashboardId: string | null) =>
    briefs.filter((s) => {
      const ids = dashboardIdsOf(s)
      // Unattributed briefs stay globally visible; attributed ones surface on
      // each dashboard they are about.
      if (ids.length === 0) return true
      return dashboardId !== null && ids.includes(dashboardId)
    })
  return {
    briefs,
    unreadCount: unread.length,
    unreadDashboardIds,
    forDashboard,
  }
}

/**
 * Reactive view over the chat sessions classified as Business-Intelligence
 * briefs (`notificationType === "bi"`). Backed by the same Dexie-driven
 * `useChatSessions` pool, so it updates live over SSE and is the single source
 * of truth for every BI surfacing level on the steering page: the nav badge
 * (`unreadCount`), the per-tab dot (`unreadDashboardIds`), and the
 * dashboard-scoped panel (`forDashboard`).
 */
export function useBiNotifications(): BiNotifications {
  const sessions = useChatSessions()
  return useMemo(() => deriveBiNotifications(sessions), [sessions])
}
